import { t } from './i18n.js';
import { useEffect, useState } from 'react';
import { api, send } from './api.js';
import { Loading, Notice } from './components.js';
import { browserZone, formatInZone, isoToLocal, localToIso, zones } from './time.js';

interface AutoReplyData {
  enabled: boolean;
  startsAt: string;
  endsAt: string | null;
  timezone: string;
  subject: string;
  body: string;
  active: boolean;
}
export function AutoReply({
  notify,
  remote,
  onSaved,
}: {
  notify: (s: string) => void;
  remote?: boolean;
  onSaved: () => void;
}) {
  const [data, setData] = useState<AutoReplyData>(),
    [enabled, setEnabled] = useState(false),
    [start, setStart] = useState(''),
    [end, setEnd] = useState(''),
    [zone, setZone] = useState(browserZone()),
    [subject, setSubject] = useState(''),
    [body, setBody] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  useEffect(() => {
    api<AutoReplyData>('/autoreply')
      .then((r) => {
        const tz = r.enabled ? r.timezone : browserZone();
        setData(r);
        setEnabled(r.enabled);
        setZone(tz);
        setStart(isoToLocal(r.startsAt, tz));
        setEnd(r.endsAt ? isoToLocal(r.endsAt, tz) : '');
        setSubject(r.subject);
        setBody(r.body);
      })
      .catch((e) => setError(e.message));
  }, []);
  if (!data && !error) return <Loading label={t('Chargement de la réponse d’absence…')} />;
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await send<AutoReplyData>(
        '/autoreply',
        {
          enabled,
          startsAt: enabled
            ? localToIso(start, zone)
            : (data?.startsAt ?? new Date().toISOString()),
          endsAt: enabled ? (end ? localToIso(end, zone) : null) : (data?.endsAt ?? null),
          timezone: enabled ? zone : (data?.timezone ?? browserZone()),
          subject,
          body,
        },
        'PUT',
      );
      setData(r);
      notify(
        r.active
          ? t('Réponse d’absence active.')
          : r.enabled
            ? t('Réponse d’absence programmée.')
            : t('Réponse d’absence désactivée.'),
      );
      onSaved();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const status = !data
    ? ''
    : data.active
      ? `Active${data.endsAt ? ` jusqu’au ${formatInZone(data.endsAt, data.timezone)}` : ', sans date de fin'}.`
      : data.enabled && new Date(data.startsAt) > new Date()
        ? `Programmée à partir du ${formatInZone(data.startsAt, data.timezone)}.`
        : data.enabled
          ? t('Période terminée : la réponse n’est plus envoyée.')
          : 'Inactive.';
  return (
    <form
      className="settings-form stack-form autoreply-form"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <p className="rules-intro">
        {t(
          t(
            'Pendant votre absence, chaque correspondant qui vous écrit directement reçoit une seule réponse automatique par semaine. Les listes de diffusion, les messages automatiques et les indésirables ne reçoivent jamais de réponse.',
          ),
        )}
      </p>
      {remote && (
        <Notice type="info">
          {t(
            enabled
              ? 'Votre réponse d’absence sera active après synchronisation.'
              : 'La désactivation de votre réponse d’absence sera transmise au serveur après enregistrement.',
          )}
        </Notice>
      )}
      {error && <Notice type="error">{error}</Notice>}
      <p className="autoreply-status" role="status">
        <strong>{t('État :')}</strong> {status}
      </p>
      <label className="check-row">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          data-testid="autoreply-enabled"
        />
        {t('Activer la réponse d’absence')}
      </label>
      <div className="autoreply-dates">
        <div>
          <label htmlFor="autoreply-start">{t('Début')}</label>
          <input
            id="autoreply-start"
            type="datetime-local"
            required={enabled}
            disabled={!enabled}
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="autoreply-end">
            {t('Fin')} <small>(facultative)</small>
          </label>
          <input
            id="autoreply-end"
            type="datetime-local"
            disabled={!enabled}
            value={end}
            min={start}
            onChange={(e) => setEnd(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="autoreply-zone">{t('Fuseau horaire')}</label>
          <input
            id="autoreply-zone"
            list="autoreply-zones"
            disabled={!enabled}
            value={zone}
            onChange={(e) => setZone(e.target.value)}
          />
          <datalist id="autoreply-zones">
            {[...new Set([...zones, zone])].map((z) => (
              <option key={z} value={z} />
            ))}
          </datalist>
        </div>
      </div>
      <label htmlFor="autoreply-subject">{t('Objet de la réponse')}</label>
      <input
        id="autoreply-subject"
        required={enabled}
        maxLength={200}
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
      />
      <label htmlFor="autoreply-body">{t('Message')}</label>
      <textarea
        id="autoreply-body"
        required={enabled}
        rows={7}
        maxLength={5000}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={t('Bonjour, je suis absent(e) jusqu’au … Je vous répondrai à mon retour.')}
      />
      <button type="submit" className="primary" disabled={busy}>
        {busy ? t('Enregistrement…') : t('Enregistrer')}
      </button>
    </form>
  );
}
